import { DatePipe } from '@angular/common'
import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { AppBaseComponent } from '../../../shared/components/app-base.component'
import { AppProject } from '../../../shared/models/interfaces/app-project.interface'
import { AppEditProjectRequest } from '../../../shared/models/interfaces/app-edit-project-request.interface'
import { ButtonModule } from 'primeng/button'
import { EpProposalDetailTabComponent } from './ep-proposal-detail-tab/ep-proposal-detail-tab.component'
import { MatTabsModule } from '@angular/material/tabs'

@Component({
   selector: 'ep-proposal',
   templateUrl: './ep-proposal.component.html',
   standalone: true,
   imports: [MatTabsModule, EpProposalDetailTabComponent, ButtonModule]
})
export class EpProposalComponent extends AppBaseComponent {
   @Input() project?: AppProject | null

   titleFC?: FormControl
   acronymFC?: FormControl
   foundingFC?: FormControl
   durationFC?: FormControl
   deadlineFC?: FormControl
   typeOfActionFC?: FormControl
   callOfTheProposalFC?: FormControl
   generalObjectivesFC?: FormControl

   editProjectFrom?: FormGroup

   typeOfActionArray = [
      { id: 'RIA', name: 'RIA - Research & Innovation Action' },
      { id: 'IA', name: 'IA - Innovation Action' },
      { id: 'CSA', name: 'CSA - Coordination & Support Action' },
      { id: 'OTHER', name: 'Other' }
   ]

   @Output() hasUnsavedChanges = new EventEmitter()
   @Output() updateProjectDetailClick = new EventEmitter<AppEditProjectRequest>()

   constructor(private _date: DatePipe) {
      super()
      this.initListener()
   }

   ngOnChanges(changes: SimpleChanges): void {
      if (changes['project'] && this.project) {
         this.initForm(this.project)
      }
   }

   saveClick() {
      if (!this.editProjectFrom?.valid) {
         this.editProjectFrom?.markAllAsTouched()
         if (this.editProjectFrom) {
            Object.keys(this.editProjectFrom.controls).forEach((key) => {
               this.editProjectFrom!!.get(key)?.markAsDirty()
            })
         }
         return
      }

      let request: AppEditProjectRequest = {
         duration: +this.durationFC?.value,
         fundingRate: this.foundingFC?.value,
         callOfTheProposal: this.callOfTheProposalFC?.value ?? '',
         generalObjectives: this.generalObjectivesFC?.value ?? '',
         title: this.titleFC?.value.trim(),
         acronym: this.acronymFC?.value.trim(),
         typeOfAction: this.typeOfActionFC?.value,
         deadline: this._date.transform(this.deadlineFC?.value, 'YYYY-MM-dd') ?? undefined
      }

      this.updateProjectDetailClick.emit(request)
   }

   private initForm(project: AppProject) {
      this.foundingFC = new FormControl(project.fundingRate ?? 0, Validators.max(100))
      this.callOfTheProposalFC = new FormControl(project.callOfTheProposal ?? null, Validators.maxLength(2048))
      this.generalObjectivesFC = new FormControl(project.generalObjectives ?? null)
      this.titleFC = new FormControl(project.title ?? null, Validators.required)
      this.acronymFC = new FormControl(project.acronym ?? null, [Validators.required, Validators.maxLength(100)])
      this.durationFC = new FormControl(project.duration ?? null, [Validators.required, Validators.pattern('^[0-9]*$'), Validators.min(1), Validators.max(180)])
      this.typeOfActionFC = new FormControl(project.typeOfAction ?? null, [Validators.required])
      this.deadlineFC = new FormControl(project.deadline, [Validators.required])

      this.editProjectFrom = new FormGroup({
         founding: this.foundingFC,
         callofTheProposal: this.callOfTheProposalFC,
         generalObjectives: this.generalObjectivesFC,
         title: this.titleFC,
         acronym: this.acronymFC,
         duration: this.durationFC,
         typeOfAction: this.typeOfActionFC,
         deadline: this.deadlineFC
      })

      this.listener?.add(
         this.editProjectFrom.valueChanges.subscribe((v) => {
            this.hasUnsavedChanges.emit(v && this.project && !this.areFormValuesEqualToProject(v, this.project))
         })
      )
   }

   private areFormValuesEqualToProject(v: any, project: AppProject): boolean {
      return (
         v.founding == project.fundingRate &&
         v.callofTheProposal == project.callOfTheProposal &&
         v.generalObjectives == project.generalObjectives &&
         v.title == project.title &&
         v.acronym == project.acronym &&
         v.duration == project.duration &&
         v.typeOfAction == project.typeOfAction &&
         (v.deadline instanceof Date ? v.deadline.toISOString().slice(0, 10) : v.deadline) ==
            (project.deadline ? new Date(project.deadline).toISOString().slice(0, 10) : null)
      )
   }
}
